/* AanbodForm — aanbod aanmaken + verzenden met inline preview-dialog.
 * Extracted from AanmeldingenPage.jsx (iter72 refactor).
 */
import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Eye, X } from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { localToday } from "@/lib/format";
import DateField from "@/components/DateField";

const DAG_LABELS = { 0: "Ma", 1: "Di", 2: "Wo", 3: "Do", 4: "Vr", 5: "Za", 6: "Zo" };

export default function AanbodForm({ id, doc, groups, onDone }) {
  const [form, setForm] = useState({
    group_id: doc.aanbod_group_id || "",
    startdatum: doc.gewenste_startdatum || localToday(),
    dagen: doc.gewenste_dagen || [],
    uurtarief: 9.65,
    geldig_dagen: 7,
    boodschap: "",
  });
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveName, setSaveName] = useState("");
  const locGroups = groups.filter(g => g.location_id === doc.location_id);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/aanbod-templates");
      setTemplates(data || []);
    } catch (e) { console.warn("Kon aanbod-templates niet laden:", e); }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const applyTemplate = (t) => {
    setForm({ ...form, boodschap: t.boodschap || "", uurtarief: t.uurtarief, geldig_dagen: t.geldig_dagen });
    toast.success(`Template '${t.name}' toegepast`);
  };
  const saveTemplate = async () => {
    if (!saveName.trim()) { toast.error("Naam verplicht"); return; }
    try {
      await api.post("/aanbod-templates", {
        name: saveName.trim(), boodschap: form.boodschap,
        uurtarief: form.uurtarief, geldig_dagen: form.geldig_dagen,
      });
      toast.success("Template opgeslagen");
      setSaveName("");
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteTemplate = async (tid) => {
    if (!window.confirm("Template verwijderen?")) return;
    try {
      await api.delete(`/aanbod-templates/${tid}`);
      toast.success("Template verwijderd");
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!form.group_id) {
      toast.error("Kies een groep voordat je het aanbod verstuurt");
      return;
    }
    if (!form.dagen || form.dagen.length === 0) { toast.error("Kies dagen"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/aanmeldingen/${id}/aanbod`, form);
      toast.success("Aanbod verstuurd — ouder ontvangt e-mail");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  // Demo-mode: laat de directeur exact zien wat de ouder in de mail te zien krijgt
  // vóór echte verzending. /aanbod/preview is read-only.
  const [previewData, setPreviewData] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const openPreview = async () => {
    if (!form.group_id) { toast.error("Kies een groep"); return; }
    if (!form.dagen || form.dagen.length === 0) { toast.error("Kies dagen"); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.post(`/aanmeldingen/${id}/aanbod/preview`, form);
      setPreviewData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setPreviewBusy(false);
  };

  return (
    <div className="p-4 rounded-md bg-blue-50 border border-blue-200 space-y-3" data-testid="aanbod-form">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">Maak een aanbod</div>
        {templates.length > 0 && (
          <Select value="" onValueChange={(v) => {
            const t = templates.find(x => x.id === v);
            if (t) applyTemplate(t);
          }}>
            <SelectTrigger className="w-52 h-8 text-xs" data-testid="template-select">
              <SelectValue placeholder="Kies template…" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {templates.map(t => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name} · €{t.uurtarief}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {templates.length > 0 && (
        <div className="flex flex-wrap gap-1 text-xs">
          <span className="text-neutral-500">Beheer:</span>
          {templates.map(t => (
            <button key={t.id} type="button" onClick={() => deleteTemplate(t.id)}
              data-testid={`del-template-${t.id}`}
              className="px-2 py-0.5 rounded bg-white border border-neutral-200 text-neutral-600 hover:border-red-300 hover:text-red-600 transition-colors">
              {t.name} ×
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={!form.group_id ? "text-red-700" : ""}>
            Groep {!form.group_id && <span className="text-red-600">*</span>}
          </Label>
          <Select value={form.group_id} onValueChange={v => setForm({ ...form, group_id: v })}>
            <SelectTrigger data-testid="aanbod-group"
                           className={!form.group_id ? "border-red-400 ring-2 ring-red-100" : ""}>
              <SelectValue placeholder="Kies groep" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {locGroups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {!form.group_id && (
            <div className="text-[11px] text-red-700 mt-1" data-testid="aanbod-group-error">
              Verplicht — kies eerst een groep voordat je het aanbod verstuurt
            </div>
          )}
        </div>
        <div><Label>Startdatum *</Label>
          <DateField value={form.startdatum} onChange={(v) => setForm({ ...form, startdatum: v })} min={localToday()} data-testid="aanbod-startdatum" />
        </div>
        <div><Label>Uurtarief (€)</Label>
          <Input type="number" step="0.01" value={form.uurtarief} onChange={e => setForm({ ...form, uurtarief: parseFloat(e.target.value) })} />
        </div>
        <div><Label>Aanbod geldig (dagen)</Label>
          <Input type="number" value={form.geldig_dagen} onChange={e => setForm({ ...form, geldig_dagen: parseInt(e.target.value) })} />
        </div>
      </div>
      <div>
        <Label>Dagen ({form.dagen.length})</Label>
        <div className="text-xs text-neutral-500">
          {form.dagen.length > 0
            ? form.dagen.map((d, i) => <span key={i} className="inline-block px-2 py-0.5 rounded bg-white border mr-1 mb-1">{DAG_LABELS[d.weekday]} · {d.dagdeel}</span>)
            : <span className="italic">Overgenomen uit aanmelding. Aanpassen? Wijzig via correctie na versturing.</span>}
        </div>
      </div>
      <div>
        <Label>Persoonlijke boodschap (optioneel)</Label>
        <Textarea rows={3} value={form.boodschap} onChange={e => setForm({ ...form, boodschap: e.target.value })}
          placeholder="Bijv. We kijken uit naar de kennismaking op…" data-testid="aanbod-boodschap" />
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Input placeholder="Sla huidige waarden op als template…" value={saveName}
            onChange={e => setSaveName(e.target.value)} data-testid="save-template-name" className="text-xs h-8" />
          <Button size="sm" variant="outline" onClick={saveTemplate} disabled={!saveName.trim()} data-testid="save-template-btn">
            Opslaan
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={openPreview} disabled={previewBusy} variant="outline"
                data-testid="btn-preview-aanbod">
          {previewBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-1" /> Voorbeeld mail</>}
        </Button>
        <Button onClick={submit} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="btn-verstuur-aanbod">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Verstuur aanbod naar ouder</>}
        </Button>
      </div>
      {previewData && (
        // Custom overlay i.p.v. geneste Radix Dialog — voorkomt z-50 collision
        // met de omliggende AanmeldingDetail-modal (beide zetten default z-50,
        // waardoor de preview onder de ouder-overlay half zichtbaar bleef).
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="aanbod-preview-dialog"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewData(null); }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-200">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Voorbeeld aanbodmail</h3>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold uppercase">preview</span>
                    <span>Er is nog geen aanbod aangemaakt en geen mail verzonden.</span>
                  </div>
                </div>
                <button
                  onClick={() => setPreviewData(null)}
                  className="p-2 rounded hover:bg-slate-100 text-slate-500"
                  aria-label="Sluiten"
                  data-testid="aanbod-preview-close-icon"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm overflow-y-auto">
              <div className="grid grid-cols-[100px_1fr] gap-1 text-xs">
                <div className="text-slate-500">Aan:</div>
                <div className="font-mono break-all">{previewData.to}</div>
                <div className="text-slate-500">Onderwerp:</div>
                <div>{previewData.subject}</div>
                <div className="text-slate-500">Geldig tot:</div>
                <div>
                  {previewData.computed?.valid_until?.slice(0, 10)} · {previewData.computed?.uren_per_week}u/w
                </div>
              </div>
              <div
                className="border border-slate-200 rounded-md p-4 bg-white"
                data-testid="aanbod-preview-html"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.html, { USE_PROFILES: { html: true } }) }}
              />
              <div className="text-xs text-slate-500 italic">{previewData.let_op}</div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end">
              <Button variant="outline" onClick={() => setPreviewData(null)} data-testid="aanbod-preview-close">
                Sluiten
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
